/* O'right shared cart engine.
   Adds via /cart/add.js with section rendering, opens Dawn's cart-drawer,
   and publishes cartUpdate. Drives every add surface via data attributes:
   - [data-oright-add="<variantId>"]      single-variant quick add / add to bag
   - [data-oright-picker-toggle]          opens the sibling .oright-picker
   - [data-oright-variant="<variantId>"]  add a chosen variant (inside a picker)
   Product form (PDP buy box):
   - form[data-oright-add-form]           intercept submit, add selected variant + qty
   - [data-oright-form-add="<formId>"]    external button (sticky bar) that adds from a form
   Multi-item form (ritual builder):
   - form[data-oright-add-multi]          intercept submit, add all items[] lines at once
   Header bag:
   - [data-oright-bag]        opens the drawer instead of navigating
   - [data-oright-bag-count]  count, updated on cartUpdate
   Requires Dawn globals (loaded earlier): routes, publish, subscribe, PUB_SUB_EVENTS. */
(function () {
  function getDrawer() {
    return document.querySelector('cart-drawer');
  }

  function closeAllPickers() {
    document.querySelectorAll('.oright-picker.is-open').forEach(function (p) {
      p.classList.remove('is-open');
    });
  }

  // --- Fix: "There was an error while updating your cart" on qty change / remove.
  // The O'right header (sections/oright-header.liquid) replaces Dawn's
  // #cart-icon-bubble cart link with a custom bag ([data-oright-bag-count],
  // synced by the cartUpdate subscriber below), so there is NO #cart-icon-bubble
  // in the DOM. Dawn's CartItems.updateQuantity (the qty stepper + Remove path in
  // cart.js) re-renders every entry from getSectionsToRender() and dereferences
  // each target WITHOUT a null check (unlike CartDrawer.renderContents, which
  // guards with `if (!sectionElement) return`). It hits
  // getElementById('cart-icon-bubble') -> null -> .querySelector(...) -> throws;
  // the change promise rejects into .catch() and the drawer shows the generic
  // error even though the line was already removed (the drawer body, rendered
  // first, succeeds - which is why the cart still looks correct).
  // Fix: drop sections whose target element is absent, so the unguarded loop only
  // touches elements that exist. The header count still updates from the
  // cartUpdate event (item_count fallback). Covers the drawer (cart-drawer-items)
  // and the stock cart page (cart-items), which share this header.
  function filterMissingSections(ctor) {
    if (!ctor || !ctor.prototype) return;
    if (Object.prototype.hasOwnProperty.call(ctor.prototype, '__orightSectionFilter')) return;
    var original = ctor.prototype.getSectionsToRender;
    if (typeof original !== 'function') return;
    ctor.prototype.getSectionsToRender = function () {
      return original.call(this).filter(function (section) {
        return document.getElementById(section.id);
      });
    };
    ctor.prototype.__orightSectionFilter = true;
  }
  if (window.customElements && customElements.whenDefined) {
    // 'cart-notification' covers the (currently inactive) notification cart_type,
    // which shares the same unguarded cart-icon-bubble pattern. It safely no-ops
    // while cart_type is 'drawer' (cart-notification.js is not loaded, so the
    // element is never defined and the patch simply never applies).
    ['cart-items', 'cart-drawer-items', 'cart-notification'].forEach(function (tag) {
      customElements
        .whenDefined(tag)
        .then(function (ctor) {
          // Some browsers resolve whenDefined with undefined; fall back to get().
          filterMissingSections(ctor || customElements.get(tag));
        })
        .catch(function () {});
    });
  }

  // Briefly swap an add button to a "✓ Added" confirmation (mirrors the design).
  function flashAdded(trigger) {
    if (!trigger || trigger.dataset.orightFlashing) return;
    trigger.dataset.orightFlashing = '1';
    var original = trigger.innerHTML;
    trigger.innerHTML =
      '<span style="display:inline-flex;align-items:center;gap:8px">' +
      '<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"></path></svg><span data-oright-added-label></span></span>';
    trigger.querySelector('[data-oright-added-label]').textContent = window.orightStrings.added;
    setTimeout(function () {
      trigger.innerHTML = original;
      delete trigger.dataset.orightFlashing;
    }, 1800);
  }

  // Add the selected variant + quantity from a single-product form.
  function addFromForm(form, trigger) {
    if (!form) return;
    var data = new FormData(form);
    var id = data.get('id');
    var qty = parseInt(data.get('quantity'), 10);
    if (isNaN(qty) || qty < 1) qty = 1;
    addToCart(id, trigger, qty, true);
  }

  // Add every line in a multi-item form (items[N][id] / items[N][quantity]),
  // e.g. the ritual builder "Add ritual to bag". The form already carries the
  // correct fields, so post its own FormData straight through.
  function addItemsFromForm(form, trigger) {
    if (!form) return;
    var formData = new FormData(form);
    if (!formData.get('items[0][id]') && !formData.get('id')) return;
    postAdd(formData, trigger, true);
  }

  // Add a single variant + quantity.
  function addToCart(variantId, trigger, quantity, flash) {
    if (!variantId) return;
    var formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', quantity || 1);
    postAdd(formData, trigger, flash);
  }

  // Shared add path: takes a ready FormData (single `id`/`quantity` or an
  // `items[]` array), posts to /cart/add.js with section rendering, then opens
  // Dawn's cart-drawer + publishes cartUpdate. Used by every add surface.
  function postAdd(formData, trigger, flash) {
    var drawer = getDrawer();
    if (trigger) trigger.classList.add('is-loading');

    // Mirror Dawn's product-form.js request exactly so the storefront
    // returns rendered `sections` (needed for the drawer to render + open).
    var config = (typeof fetchConfig === 'function')
      ? fetchConfig('javascript')
      : { method: 'POST', headers: { Accept: 'application/javascript' } };
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    if (drawer) {
      formData.append('sections', drawer.getSectionsToRender().map(function (s) { return s.id; }));
      formData.append('sections_url', window.location.pathname);
    }
    config.body = formData;

    fetch(window.routes.cart_add_url, config)
      .then(function (r) { return r.json(); })
      .then(function (response) {
        if (response.status) {
          console.error('O’right add to cart error:', response.description || response.message);
          return;
        }
        if (drawer) {
          drawer.renderContents(response);
          // We just added an item, so the bag is no longer empty. Dawn's
          // renderContents only clears is-empty off .drawer__inner, not the
          // <cart-drawer> element, so when adding from an empty cart the stale
          // is-empty would hide all the (now-populated) drawer content and make
          // trapFocus look for the absent .drawer__inner-empty. Clear it here.
          drawer.classList.remove('is-empty');
        } else {
          window.location = window.routes.cart_url;
          return;
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'oright-cart', cartData: response });
        }
        if (flash) flashAdded(trigger);
      })
      .catch(function (e) { console.error(e); })
      .finally(function () {
        if (trigger) trigger.classList.remove('is-loading');
        closeAllPickers();
      });
  }

  // Delegated clicks
  document.addEventListener('click', function (evt) {
    var addBtn = evt.target.closest('[data-oright-add]');
    if (addBtn) {
      evt.preventDefault();
      addToCart(addBtn.getAttribute('data-oright-add'), addBtn);
      return;
    }
    var variantBtn = evt.target.closest('[data-oright-variant]');
    if (variantBtn) {
      evt.preventDefault();
      addToCart(variantBtn.getAttribute('data-oright-variant'), variantBtn);
      return;
    }
    var formAddBtn = evt.target.closest('[data-oright-form-add]');
    if (formAddBtn) {
      evt.preventDefault();
      var ref = formAddBtn.getAttribute('data-oright-form-add');
      var refForm = ref ? document.getElementById(ref) : formAddBtn.closest('form');
      addFromForm(refForm, formAddBtn);
      return;
    }
    var pickerClose = evt.target.closest('[data-oright-picker-close]');
    if (pickerClose) {
      // The size-picker close affordances: the (x) button and, on the mobile
      // overlay sheet, the dimmed scrim. Both carry data-oright-picker-close.
      evt.preventDefault();
      closeAllPickers();
      return;
    }
    var toggle = evt.target.closest('[data-oright-picker-toggle]');
    if (toggle) {
      evt.preventDefault();
      var picker = toggle.parentElement.querySelector('.oright-picker');
      var willOpen = picker && !picker.classList.contains('is-open');
      closeAllPickers();
      if (willOpen) picker.classList.add('is-open');
      return;
    }
    if (!evt.target.closest('.oright-picker')) closeAllPickers();
  });

  document.addEventListener('keyup', function (evt) {
    if (evt.code === 'Escape') closeAllPickers();
  });

  // Intercept add-to-cart form submits (progressive enhancement: native POST
  // falls back to /cart/add when JS is off). Covers the PDP product form
  // (single item) and the ritual builder (multi item).
  document.addEventListener('submit', function (evt) {
    var multiForm = evt.target.closest('form[data-oright-add-multi]');
    if (multiForm) {
      evt.preventDefault();
      addItemsFromForm(multiForm, evt.submitter || multiForm.querySelector('[type="submit"]'));
      return;
    }
    var form = evt.target.closest('form[data-oright-add-form]');
    if (!form) return;
    evt.preventDefault();
    addFromForm(form, evt.submitter || form.querySelector('[type="submit"]'));
  });

  // Header bag: open the drawer instead of navigating to /cart
  function wireBag() {
    var bag = document.querySelector('[data-oright-bag]');
    var drawer = getDrawer();
    if (bag && drawer) {
      bag.addEventListener('click', function (e) {
        e.preventDefault();
        drawer.open(bag);
      });
    }
  }
  if (document.readyState !== 'loading') wireBag();
  else document.addEventListener('DOMContentLoaded', wireBag);

  // Header count: update from the re-rendered cart-icon-bubble section
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      var data = event && event.cartData;
      var countEl = document.querySelector('[data-oright-bag-count]');
      if (!countEl) return;
      var count;
      if (data && data.sections && data.sections['cart-icon-bubble']) {
        var doc = new DOMParser().parseFromString(data.sections['cart-icon-bubble'], 'text/html');
        var span = doc.querySelector('.cart-count-bubble span[aria-hidden="true"]');
        if (span) count = span.textContent.trim();
      }
      if ((count == null || count === '') && data && typeof data.item_count === 'number') {
        count = data.item_count;
      }
      if (count != null && count !== '') {
        // Treat 0 as empty. The header badge is hidden via the [hidden] attribute
        // when the cart is empty (oright-header.liquid), so only show it when > 0.
        // This matters now that removes no longer throw: the cartUpdate event also
        // fires on remove-to-empty, and we must re-hide the badge rather than
        // display a "0". (Previously the throw stopped this path from running.)
        var n = Number(count);
        countEl.textContent = count;
        countEl.hidden = isNaN(n) ? false : n <= 0;
      }
    });
  }
})();
